import {useEffect, useMemo, useState} from 'react';
import {BookOpen, ChevronDown, ChevronRight, FileText, X, Search, ChartNoAxesCombined, PenTool, BriefcaseBusiness, Code2, Headset, Workflow, ShieldCheck, Play, Copy} from 'lucide-react';
import type {LucideIcon} from 'lucide-react';
import type {VisualEmployee} from './OrdinaryWorkforceHome';
import type {ImportedSkillRecord} from '../lib/unifiedSkillLibrary';
import auditedSkillGates from '../data/skill-audits/first59-integration-gates-20261012.json';
import acceptanceCases from '../data/skill-audits/first59-acceptance-cases-20261012.json';
import stagedSkillGuides from '../data/skill-audits/active-82-client-card-guides.json';
import next70GuidesA from '../data/skill-audits/next70-client-card-guides-part-1.json';
import next70GuidesB from '../data/skill-audits/next70-client-card-guides-part-2.json';
import {supabase} from '../lib/supabase';
import {matchesSkillEmployee} from '../lib/skillAgentSearch';
import {technicalSkillOperations} from '../data/TechnicalSkillOperations';
import {skillGroup, SKILL_GROUPS} from '../lib/skillGrouping';
import SkillCapabilityRunnerLive from './SkillCapabilityRunnerLive';
import {fiveQuarantinedSkillCards} from '../data/FiveQuarantinedSkillCards';

const roleBySkill=new Map(auditedSkillGates.entries.map(item=>[item.id,item.agentRole]));
const functionalRuntimeVerified=true;
const acceptanceBySkill=new Map(acceptanceCases.cases.map(item=>[item.skillId,item]));
const stagedGuideBySkill={...stagedSkillGuides,...next70GuidesA,...next70GuidesB} as Record<string,{requiredInputs:string;expectedEvidence:string[];recommendedRoleKey:string;recommendedRoleKeys?:string[]}>;
// Source text, paths and repositories are never taken from the card model.
// Explicit owner-authenticated backend requests are required for private details.
const GROUP_ORDER=SKILL_GROUPS;
const GROUP_ICONS:Record<string,LucideIcon>={
 'Research':Search,'Finance & Markets':ChartNoAxesCombined,
 'Marketing & Sales':BriefcaseBusiness,'Content & Publishing':PenTool,
 'Branding & Design':PenTool,'Operations & Projects':Workflow,
 'Engineering & Data':Code2,'Legal & Compliance':ShieldCheck,
 'Safety & Quality':ShieldCheck,'Product & Strategy':Workflow,
 'Agent Systems':Workflow,'Personal & Productivity':BookOpen,
 'Customer Support & Fulfillment':Headset
};
type Mode='details'|'use';
type OwnerSkillRoute={ok:true;primaryRole:string;qualifiedSupportRoles:string[];
 route:{state:string;selectedRole:string;reason:string;executeNow:false;existingOwner:boolean}};
type OwnerPrivateSource={ok:true;sourceKind:string;displayName:string;
 repository?:string|null;sourcePath?:string|null;sourceUrl?:string|null;
 attribution?:string|null;license?:string|null;skillCode?:string;truncated?:boolean};
const roleLabel=(key:string,employees:VisualEmployee[])=>{
 const agent=employees.find(e=>e.roleKey===key);
 return agent?.nickname||agent?.name||key.replaceAll('_',' ');
};
async function ownerSkillAction<T>(action:'route'|'source',skillId:string):Promise<T>{
 const {data,error}=await supabase.functions.invoke('agentopia-skill-capability-v1',{body:{action,skillId}});
 if(error||!data?.ok)throw new Error(String(data?.error||error?.message||'Owner skill backend unavailable'));
 return data as T;
}
// Employee-level holds are enforced on server tasks and skill runs.
// Never show a paused, blocked, disabled or dormant employee as runnable.
const employeeOnHold=(employee?:VisualEmployee)=>Boolean(employee&&(employee.enabled===false||/^(paused|blocked|disabled|dormant)$/i.test(employee.status||'')));
const cleanText=(value?:string)=>String(value||'').replace(/\s+/g,' ').trim();
const descriptionFor=(skill:ImportedSkillRecord)=>{
 const description=cleanText(skill.description);
 return description||'The source record has no description. Review its available instructions before use.';
};
const successFor=(skill:ImportedSkillRecord):string[]=>{
 const source=skill.kind==='imported'&&fiveQuarantinedSkillCards[skill.name]
  ?[fiveQuarantinedSkillCards[skill.name].outcome]
  :skill.kind==='profile'
  ?[skill.profileSuccess||'']
  :acceptanceBySkill.get(skill.id)?.expectedEvidence||stagedGuideBySkill[skill.id]?.expectedEvidence||[];
 const values=source.map(item=>cleanText(item)).filter(Boolean);
 return values.length?Array.from(new Set(values)):
  ['The source does not specify a successful outcome. Define measurable completion criteria before use.'];
};
const separateInputsFor=(skill:ImportedSkillRecord)=>{
 const raw=skill.kind==='imported'&&fiveQuarantinedSkillCards[skill.name]
  ?fiveQuarantinedSkillCards[skill.name].inputs
  :skill.kind==='profile'
  ?''
  :acceptanceBySkill.get(skill.id)?.requiredInputs||stagedGuideBySkill[skill.id]?.requiredInputs||'';
 const text=cleanText(raw);
 return text&&text.toLowerCase()!==descriptionFor(skill).toLowerCase()?text:'';
};
export default function ImportedSkillsCatalog({
 search,category,employeeFilter,selectedEmployeeRoleKey,liveEnabled=false,
 registryItems,loaded=false,loadError='',onCopyToDraft,employees=[],onRequestAssignment
}: {search:string;category:string;employeeFilter:string;selectedEmployeeRoleKey?:string;
 liveEnabled?:boolean;registryItems:ImportedSkillRecord[];loaded?:boolean;loadError?:string;onCopyToDraft?:(skill:ImportedSkillRecord)=>void;
 employees?:VisualEmployee[];onRequestAssignment?:(skill:ImportedSkillRecord,agent:VisualEmployee,message:string)=>Promise<boolean>}) {
 const [openGroup,setOpenGroup]=useState<string|null>('Research');
 const [counts,setCounts]=useState<Record<string,number>>({});
 const [selected,setSelected]=useState<{skill:ImportedSkillRecord;mode:Mode}|null>(null);
 const [agentSearch,setAgentSearch]=useState('');
 const [selectedAgentId,setSelectedAgentId]=useState('');
 const [assignmentNote,setAssignmentNote]=useState('');
 const [objective,setObjective]=useState('');
 const [busy,setBusy]=useState(false);
 const [advancedOpen,setAdvancedOpen]=useState(false);
 const [notice,setNotice]=useState('');
 const [lastRequest,setLastRequest]=useState<{skillId:string;taskId:string;kind:'imported'|'profile'}|null>(null);
 const [outcome,setOutcome]=useState('');
 const [checkingResult,setCheckingResult]=useState(false);
 const [ownership,setOwnership]=useState<OwnerSkillRoute|null>(null);
 const [ownershipLoading,setOwnershipLoading]=useState(false);
 const [ownershipError,setOwnershipError]=useState('');
 const [privateSource,setPrivateSource]=useState<OwnerPrivateSource|null>(null);
 const [sourceLoading,setSourceLoading]=useState(false);
 const [sourceError,setSourceError]=useState('');
 useEffect(()=>{
  const skill=selected?.skill;
  if(!skill||!liveEnabled){setOwnership(null);setOwnershipLoading(false);return}
  let cancelled=false;
  setOwnership(null);setOwnershipError('');setOwnershipLoading(true);
  void ownerSkillAction<OwnerSkillRoute>('route',skill.id).then(data=>{
   if(!cancelled)setOwnership(data);
  }).catch(e=>{if(!cancelled)setOwnershipError(e instanceof Error?e.message:'Could not verify the skill lead.')})
    .finally(()=>{if(!cancelled)setOwnershipLoading(false)});
  return ()=>{cancelled=true};
 },[selected?.skill.id,liveEnabled]);
 async function revealPrivateSource(){
  if(!selectedSkill||!liveEnabled||sourceLoading)return;
  setSourceLoading(true);setSourceError('');setPrivateSource(null);
  try{setPrivateSource(await ownerSkillAction<OwnerPrivateSource>('source',selectedSkill.id))}
  catch(e){setSourceError(e instanceof Error?e.message:'Private source is unavailable.')}
  finally{setSourceLoading(false)}
 }
 const grouped=useMemo(()=>{
  const q=search.trim().toLowerCase();
  const groups=new Map<string,ImportedSkillRecord[]>();
  for(const skill of registryItems){
   const group=skillGroup(skill);
   if(category!=='All'&&category!==group)continue;
   if(q&&![skill.name,skill.description,skill.category,group,skill.repository,skill.profileSuccess||''].join(' ').toLowerCase().includes(q))continue;
   if(employeeFilter!=='all'&&selectedEmployeeRoleKey&&
    roleBySkill.get(skill.id)!==selectedEmployeeRoleKey&&
    stagedGuideBySkill[skill.id]?.recommendedRoleKey!==selectedEmployeeRoleKey&&
    !stagedGuideBySkill[skill.id]?.recommendedRoleKeys?.includes(selectedEmployeeRoleKey)&&
    !skill.assignedRoles.includes(selectedEmployeeRoleKey)&&
    !skill.preparedRoles.includes(selectedEmployeeRoleKey))continue;
   groups.set(group,[...(groups.get(group)||[]),skill]);
  }
  return GROUP_ORDER.filter(name=>groups.has(name)).map(name=>({name,skills:groups.get(name)!}));
 },[search,category,employeeFilter,selectedEmployeeRoleKey,registryItems]);
 const total=grouped.reduce((sum,group)=>sum+group.skills.length,0);
 const selectedSkill=selected?.skill;
 const selectedAgent=employees.find(agent=>agent.id===selectedAgentId);
 const selectedAgentOnHold=employeeOnHold(selectedAgent);
 const selectedGuide=selectedSkill?stagedGuideBySkill[selectedSkill.id]:null;
 const recommendedAgent=employees.find(agent=>agent.roleKey===(ownership?.primaryRole||selectedGuide?.recommendedRoleKey));
 const authorizedForAgent=Boolean(selectedSkill&&selectedAgent&&selectedSkill.assignedRoles.includes(selectedAgent.roleKey));
 const preparedForAgent=Boolean(selectedSkill&&selectedAgent&&selectedSkill.preparedRoles.includes(selectedAgent.roleKey));
 const executable=Boolean(selectedSkill&&liveEnabled&&selectedSkill.state==='active'&&selectedSkill.ready&&authorizedForAgent&&!selectedAgentOnHold&&ownership?.ok&&selectedAgent?.roleKey===ownership.primaryRole&&ownership.route.state==='primary_qualified');
 const matchedEmployees=employees.filter(agent=>matchesSkillEmployee(agent,agentSearch));
 function show(skill:ImportedSkillRecord,mode:Mode){
  setSelected({skill,mode});setAgentSearch('');setSelectedAgentId('');setAssignmentNote('');setObjective('');setNotice('');setAdvancedOpen(false);setLastRequest(null);setOutcome('');setPrivateSource(null);setSourceError('');
 }
 async function submitRequest(e:React.FormEvent<HTMLFormElement>){
  e.preventDefault();
  if(!selectedSkill||!selectedAgent||!executable||busy)return;
  if(objective.trim().length<8){setNotice('Describe what you want the assigned employee to do.');return;}
  setBusy(true);setNotice('');
  try{
   const {data,error}=await supabase.rpc(selectedSkill.kind==='profile'?'agentopia_owner_request_profile_skill_v1':'agentopia_owner_request_external_skill',{
    p_skill_id:selectedSkill.id,p_role_key:selectedAgent.roleKey,p_objective:objective.trim(),p_request_id:crypto.randomUUID()
   });
   if(error||!data?.ok){setNotice('The request was not started: '+(error?.message||'Skill action is unavailable.'));return;}
   if(!data.taskId){setNotice('Task response has no ID; completion cannot be tracked.');return;}
   setLastRequest({skillId:selectedSkill.id,taskId:String(data.taskId),kind:selectedSkill.kind});
   setOutcome('');
   setNotice('Task accepted. Work is not complete yet. Select Check task result to verify its actual status.');
   setObjective('');
  }catch(e){setNotice(e instanceof Error?e.message:'Request could not be submitted.');}
  finally{setBusy(false);}
 }
 async function checkTaskResult(){
  if(!lastRequest||checkingResult)return;
  setCheckingResult(true);setOutcome('');
  try{
   const {data,error}=await supabase.rpc(lastRequest.kind==='profile'?'agentopia_owner_profile_skill_result_v1':'agentopia_owner_external_skill_result_v1',{p_task_id:lastRequest.taskId});
   if(error||!data?.ok){setNotice('Task status unavailable: '+(error?.message||'Owner status request was rejected.'));return;}
   const state=String(data.status||'unknown');
   const problem=data.blocker||data.error;
   setOutcome('Status: '+state+(problem?'\nBlocked: '+String(problem):'')+
     (state==='completed'?'\nVerified stored work result:\n'+String(data.result||'(no saved result)'):'\nWork has not been verified as completed.'));
   setNotice(state==='completed'?'Task marked completed in the live database.':'Task status checked. It is not yet marked completed.');
  }catch(e){setNotice(e instanceof Error?e.message:'Unable to load task result.');}
  finally{setCheckingResult(false);}
 }
 return <section id="ao-imported-skills" className="ao-existing-skills ao-imported-groups" aria-label="Unified employee and imported skill cards">
  <header className="ao-existing-heading">
   <div><strong>Skill Library · {total} unique skills</strong>
    <small>Explore skills by category, search by name, or filter by employee. Select a group to browse its skills.</small>
   </div>
  </header>
  {grouped.map((group,index)=>{
   const Icon=GROUP_ICONS[group.name]||BookOpen;
   const open=Boolean(search.trim())||category!=='All'||(openGroup===group.name)||(openGroup==='Research'&&index===0);
   const visible=(search.trim()||category!=='All')?group.skills:group.skills.slice(0,counts[group.name]||12);
   return <section className="ao-imported-group" key={group.name} aria-label={group.name+' skills'}>
    <button type="button" className="ao-imported-group-toggle" aria-expanded={open}
      onClick={()=>setOpenGroup(current=>current===group.name?null:group.name)}>
      <span className="ao-imported-group-icon"><Icon size={23}/></span>
      <span className="ao-imported-group-label"><strong>{group.name}</strong><small>{group.skills.length} skill{group.skills.length===1?'':'s'}</small></span>
      <ChevronDown size={20} className={open?'ao-group-chevron open':'ao-group-chevron'}/>
    </button>
    {open&&<div className="ao-skills-grid ao-imported-group-grid">
     {visible.map(skill=>{
      const active=liveEnabled&&skill.state==='active'&&skill.ready;
      const assignedEmployee=employees.find(agent=>agent.roleKey===stagedGuideBySkill[skill.id]?.recommendedRoleKey);
      const eligibleAssignees=employees.filter(agent=>skill.assignedRoles.includes(agent.roleKey)&&!employeeOnHold(agent));
      const status=!liveEnabled?'PREVIEW · OWNER SIGN-IN':active?(eligibleAssignees.length
       ?skill.kind==='profile'?'READY · OWNER REQUEST':'READY · RESTRICTED ANALYSIS'
       :skill.preparedRoles.length?'PREPARED · EMPLOYEE ON HOLD':'REGISTERED · UNASSIGNED'):!loaded?'CHECKING':
       skill.kind==='imported'&&skill.state==='staged'&&fiveQuarantinedSkillCards[skill.name]?'QUARANTINED · DISABLED':
       skill.state==='staged'?'STAGED · REVIEW REQUIRED':
       skill.state==='reviewed'?'UNDER REVIEW':'NOT ACTIVE';
      const evidence=successFor(skill);
      return <article className="ao-skill-card ao-skill-existing ao-imported-clean-card" key={skill.id}>
       <div className="ao-skill-card-top"><span className="ao-skill-category">{skill.kind==='profile'?'EMPLOYEE SKILL':'IMPORTED SKILL'}</span>
        <span className="ao-skill-draft">{status}</span></div>
       <div className="ao-skill-card-main"><span className="ao-skill-icon"><BookOpen size={24}/></span>
        <div><h3>{skill.name}</h3><p>{descriptionFor(skill)}</p>
        {skill.kind==='imported'&&skill.state==='staged'&&fiveQuarantinedSkillCards[skill.name]&&<p><strong>Publisher:</strong> {fiveQuarantinedSkillCards[skill.name].publisher} · <strong>License:</strong> {fiveQuarantinedSkillCards[skill.name].license}</p>}</div></div>
       <details className="ao-existing-details ao-imported-success">
        <summary>Success criteria <ChevronDown size={15}/></summary>
        <div><ul>{evidence.map(item=><li key={item}>{item}</li>)}</ul></div>
       </details>
       <div className="ao-imported-card-actions">
        <button className="ao-imported-details-button" type="button" onClick={()=>show(skill,'details')}>
         <FileText size={17}/> View details <ChevronRight size={15}/></button>
        <button className="ao-imported-use-button" type="button" disabled={skill.kind==='imported'&&skill.state==='staged'&&Boolean(fiveQuarantinedSkillCards[skill.name])} onClick={()=>show(skill,'use')}>
         <Play size={18}/> {skill.kind==='imported'&&skill.state==='staged'&&fiveQuarantinedSkillCards[skill.name]?'Not activated':'Use Skill'}</button>
       </div>
      </article>;
     })}
    </div>}
    {open&&group.skills.length>visible.length&&<button type="button" className="ao-imported-group-more" onClick={()=>setCounts(previous=>({...previous,[group.name]:group.skills.length}))}>View all {group.skills.length} {group.name} skills ({group.skills.length-visible.length} more) <ChevronDown size={16}/></button>}
   </section>;
  })}
  {!total&&loaded&&!loadError&&<div className="ao-skills-empty"><Search size={27}/><p>No skills match your search.</p></div>}
  {liveEnabled&&!loaded&&<p role="status">Checking your skill library…</p>}
  {loadError&&<p role="alert">{loadError}</p>}
  {selectedSkill&&<div className="ao-skill-detail-backdrop" role="presentation" onClick={()=>setSelected(null)}>
   <section className="ao-skill-detail-dialog ao-imported-modal" role="dialog" aria-modal="true" aria-label={selectedSkill.name+' skill'} onClick={e=>e.stopPropagation()}>
    <header><div><small>{selectedSkill.kind==='profile'?'EMPLOYEE PROFILE SKILL · ':''}{skillGroup(selectedSkill)}</small><h2>{selectedSkill.name}</h2></div>
     <button type="button" aria-label="Close skill panel" onClick={()=>setSelected(null)}><X size={21}/></button></header>
    {selected.mode==='details'?
     <><div className="ao-skill-detail-body"><h3>What this skill does</h3><p>{descriptionFor(selectedSkill)}</p>
       {selectedSkill.kind==='imported'&&selectedSkill.state==='staged'&&fiveQuarantinedSkillCards[selectedSkill.name]&&<><p role="status"><strong>Quarantined and disabled.</strong> Source review and fixture verification do not permit execution.</p><p><strong>Publisher:</strong> {fiveQuarantinedSkillCards[selectedSkill.name].publisher} · <strong>License:</strong> {fiveQuarantinedSkillCards[selectedSkill.name].license}</p><p><strong>Recommended primary:</strong> {fiveQuarantinedSkillCards[selectedSkill.name].primaryRecommendation} · <strong>Potential support:</strong> {fiveQuarantinedSkillCards[selectedSkill.name].supportRecommendation} (not a permission grant)</p><p><strong>Connections and tools:</strong> {fiveQuarantinedSkillCards[selectedSkill.name].connection}</p></>}
       {separateInputsFor(selectedSkill)&&<><h3>How it works / required inputs</h3><p>{separateInputsFor(selectedSkill)}</p></>}
       <h3>Successful outcome</h3><ul>{successFor(selectedSkill).map(t=><li key={t}>{t}</li>)}</ul>
       {selectedSkill.kind==='imported'&&<p role="note">Registry approval permits only the recorded bounded owner-requested analysis. It is not proof of an independently verified live outcome or permission for external scripts, customer contact, paid providers, or automatic jobs.</p>}
       <h3>Primary employee</h3>
       {ownershipLoading&&<p role="status">Checking the assigned specialist…</p>}
       {ownership&&<><p><strong>{roleLabel(ownership.primaryRole,employees)}</strong> · Lead agent for this skill.</p>
        <h3>Supporting employees</h3><p>{ownership.qualifiedSupportRoles.length?
         ownership.qualifiedSupportRoles.map(role=>roleLabel(role,employees)).join(', '):
         'No other qualified employee is currently mapped.'}</p>
        <p role="note">{ownership.route.reason} Only the primary specialist may start a new request. Existing work remains with its assigned owner unless a handoff is approved.</p></>}
       {!ownershipLoading&&!ownership&&<p role="alert">The primary employee has not been verified. {ownershipError||'An authenticated owner connection is required.'} No job can start without this check.</p>}
       {selectedSkill.kind==='profile'&&<p>{selectedSkill.assignedRoles.length} currently eligible employee assignment(s). Original shared skill mapping is preserved.</p>}
       {selectedSkill.kind==='imported'&&<p>{selectedSkill.assignedRoles.length} enabled assignment(s); {Math.max(0,selectedSkill.preparedRoles.length-selectedSkill.assignedRoles.length)} prepared and disabled. Employee safety holds and approved roles still apply.</p>}
       <div className="ao-imported-source-details">
        <button type="button" aria-label={'View private backend for '+selectedSkill.name} disabled={!liveEnabled||sourceLoading}
         onClick={()=>void revealPrivateSource()}><ShieldCheck size={16}/> {sourceLoading?'Checking owner permission…':'Owner-only backend and source'}</button>
        <p>Skill instructions, underlying code, repository and origin stay behind your owner authentication. Nothing here activates a skill.</p>
        {sourceError&&<p role="alert">{sourceError}</p>}
        {privateSource&&<div className="ao-private-skill-backend" role="region" aria-label="Private skill backend details">
         <p><strong>Origin:</strong> {privateSource.sourceKind==='internal_employee_profile'?'Agentordinary internal employee profile':'Imported skill source'}</p>
         {privateSource.repository&&<p><strong>Repository:</strong> {privateSource.repository}</p>}
         {privateSource.sourcePath&&<p><strong>Source path:</strong> {privateSource.sourcePath}</p>}
         {privateSource.license&&<p><strong>License:</strong> {privateSource.license}</p>}
         {privateSource.attribution&&<p><strong>Attribution:</strong> {privateSource.attribution}</p>}
         {privateSource.sourceUrl&&/^https:\/\//.test(privateSource.sourceUrl)&&
          <a href={privateSource.sourceUrl} target="_blank" rel="noopener noreferrer">View original documentation</a>}
         <pre tabIndex={0} style={{whiteSpace:'pre-wrap',overflowWrap:'anywhere',maxHeight:350,overflowY:'auto'}}>{privateSource.skillCode||'No backend source text is stored for this skill.'}</pre>
         {privateSource.truncated&&<p>Only the first portion of this long source record is shown.</p>}
        </div>}
       </div>
      </div><footer>{onCopyToDraft&&<button type="button" onClick={()=>{onCopyToDraft(selectedSkill);setSelected(null)}}><Copy size={16}/> Edit a copy</button>}
       <button type="button" onClick={()=>show(selectedSkill,'use')}><Play size={16}/> Use Skill</button></footer></>:
     <div className="ao-skill-agent-flow">
      <p>Select the employee who should use this skill. Search by name, nickname, or role.</p>
      <label className="ao-skill-agent-search"><Search size={18}/>
       <input type="search" value={agentSearch} onChange={e=>setAgentSearch(e.target.value)}
        placeholder="Search employees by name or role" aria-label="Search agents to use or receive this skill"/>
      </label>
      <div className="ao-skill-agent-results" role="group" aria-label="Choose an AgentOrdinary employee">
       {matchedEmployees.slice(0,15).map(agent=><button type="button" key={agent.id}
        className={selectedAgentId===agent.id?'active':''}
        aria-pressed={selectedAgentId===agent.id}
        onClick={()=>{setSelectedAgentId(agent.id);setNotice('');}}>
        <span><strong>{agent.nickname||agent.name}</strong><small>{agent.role}</small></span>
        {selectedAgentId===agent.id?<ShieldCheck size={18}/>:<ChevronRight size={18}/>}
       </button>)}
       {!matchedEmployees.length&&<p>No matching employees.</p>}
       {matchedEmployees.length>15&&<small>Refine your search to see more employees.</small>}
      </div>
      {!selectedAgent&&<p className="ao-agent-pick-note">Choose an employee to continue.</p>}
      {selectedAgent&&selectedAgentOnHold&&<p role="status">This employee is currently blocked or paused. {preparedForAgent?'The skill mapping is saved, but its assignment is not runnable.':'A skill assignment has not been authorized.'} The hold must be resolved through existing workforce safety controls before work can start.</p>}
      {selectedAgent&&!selectedAgentOnHold&&preparedForAgent&&!authorizedForAgent&&
       <p role="status">This employee already has a prepared assignment. It remains disabled under the existing workforce or safety policy. No duplicate assignment request is needed.</p>}
      {selectedAgent&&!selectedAgentOnHold&&!preparedForAgent&&(!liveEnabled||!authorizedForAgent||selectedSkill.state!=='active'||!selectedSkill.ready)&&
       <section className="ao-agent-assignment-request">
        <h3>Ask Praamis to give {selectedAgent.nickname||selectedAgent.name} this skill</h3>
        <p>This saves a request for review. It does not automatically assign, activate, or run a skill.</p>
        <label>Message to Praamis<textarea rows={3} maxLength={800} value={assignmentNote}
         onChange={e=>setAssignmentNote(e.target.value)}
         placeholder={`@Praamis, please review assigning ${selectedSkill.name} to ${selectedAgent.nickname||selectedAgent.name}.`}/></label>
        <button type="button" disabled={!onRequestAssignment||busy} onClick={async()=>{
         if(!onRequestAssignment)return;
         setBusy(true);setNotice('');
         try{
          const message=assignmentNote.trim()||`@Praamis, please review assigning ${selectedSkill.name} to ${selectedAgent.nickname||selectedAgent.name}.`;
          const saved=await onRequestAssignment(selectedSkill,selectedAgent,message);
          setNotice(saved?'Request saved for review. No skill permission has been granted.':'The request was not saved.');
         }catch(e){setNotice(e instanceof Error?e.message:'Could not save the assignment request.');}
         finally{setBusy(false);}
        }}> {busy?'Saving…':'Save request to Praamis'} </button>
       </section>}
      {selectedAgent&&executable&&
       <section className="ao-agent-ready">
        <p><ShieldCheck size={17}/> {selectedAgent.nickname||selectedAgent.name} is authorized for this skill.</p>
        {functionalRuntimeVerified&&technicalSkillOperations[selectedSkill.id]?
         <><button className="ao-agent-configure" type="button"
           onClick={()=>setAdvancedOpen(v=>!v)} aria-expanded={advancedOpen}>
           {advancedOpen?'Hide workflow setup':'Choose a workflow'} <ChevronDown size={16}/></button>
          {advancedOpen&&<SkillCapabilityRunnerLive skillId={selectedSkill.id} available={executable}/>}</>:
         <form className="ao-imported-use-form" onSubmit={submitRequest}>
          <label>What should {selectedAgent.nickname||selectedAgent.name} do?
           <textarea rows={4} maxLength={1200} value={objective}
            onChange={e=>setObjective(e.target.value)} placeholder="Describe the task and expected outcome"/></label>
          <button type="submit" disabled={busy||objective.trim().length<8}>
           <Play size={16}/>{busy?'Submitting…':'Send task to '+(selectedAgent.nickname||selectedAgent.name)}</button>
         </form>}
       </section>}
     </div>}
    {notice&&<p className="ao-imported-modal-notice" role="status">{notice}</p>}
    {selectedSkill&&lastRequest?.skillId===selectedSkill.id&&<section className="ao-agent-ready" aria-label="Verify actual task outcome">
     <button type="button" disabled={checkingResult} onClick={checkTaskResult}>{checkingResult?'Checking…':'Check task result'}</button>
     {outcome&&<pre style={{whiteSpace:'pre-wrap',overflowWrap:'anywhere',maxHeight:260,overflowY:'auto'}} aria-live="polite">{outcome}</pre>}
    </section>}
   </section>
  </div>}
 </section>;
}
